import React, { useCallback, useEffect, useMemo, useState } from "react"
import axios from "axios"
import { useActiveClinic } from "../../../context/ClinicContext"
import { useClinicPlanSnapshot } from "../../subscriptions/hooks/useClinicPlanSnapshot"
import { useClinicSubscription } from "../../subscriptions/hooks/useClinicSubscription"
import "../../../shared/styles/settings-system.css"

const roleOptions = [
  { value: "ADMIN", label: "Administrador" },
  { value: "DOCTOR", label: "Doctor" },
  { value: "RECEPTIONIST", label: "Recepcionista" },
  { value: "ASSISTANT", label: "Asistente" },
  { value: "FINANCE", label: "Financiero" }
]

export default function UsersPage() {
  const { activeClinicId } = useActiveClinic()

  const snapshotClinicId =
    activeClinicId && activeClinicId !== "ALL" ? activeClinicId : null

  const [users, setUsers] = useState([])
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [role, setRole] = useState("ASSISTANT")
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(false)

  const {
    data: planSnapshot,
    refresh: refreshPlanSnapshot
  } = useClinicPlanSnapshot(snapshotClinicId)

  const { subscription } = useClinicSubscription(snapshotClinicId)

  const maxUsers = planSnapshot?.plan?.maxUsers
  const usersCount = planSnapshot?.usage?.usersCount ?? 0
  const accountName = planSnapshot?.accountName || "tu cuenta"

  const limitReached =
    maxUsers !== null &&
    maxUsers !== undefined &&
    usersCount >= maxUsers

  const isReadOnly = subscription?.access?.isReadOnly
  const readOnlyReason = subscription?.access?.reason

  const canCreate =
    !!snapshotClinicId &&
    !saving &&
    !limitReached &&
    !isReadOnly &&
    !!name.trim() &&
    !!email.trim()

  const loadUsers = useCallback(async () => {
    if (!snapshotClinicId) {
      setUsers([])
      return
    }

    setLoading(true)
    setError(null)

    try {
      const { data } = await axios.get("/api/multi-clinic/clinic-users", {
        params: { clinicId: snapshotClinicId }
      })

      setUsers(Array.isArray(data) ? data : [])
    } catch (err) {
      const message =
        err?.response?.data?.message || "No se pudieron cargar los usuarios"
      setError(Array.isArray(message) ? message[0] : message)
    } finally {
      setLoading(false)
    }
  }, [snapshotClinicId])

  useEffect(() => {
    loadUsers()
  }, [loadUsers])

  async function handleCreate() {
    try {
      setSaving(true)
      setError(null)
      setSuccess(null)

      await axios.post("/api/multi-clinic/add-user", {
        clinicId: snapshotClinicId,
        name: name.trim(),
        email: email.trim(),
        role
      })

      setName("")
      setEmail("")
      setRole("ASSISTANT")

      await loadUsers()
      await refreshPlanSnapshot()

      window.dispatchEvent(new Event("odontoflow:clinics-updated"))
      window.dispatchEvent(new Event("odontoflow:subscription-updated"))

      setSuccess("Usuario creado correctamente.")
    } catch (err) {
      const message =
        err?.response?.data?.message || "No se pudo crear el usuario"

      setError(Array.isArray(message) ? message[0] : message)
    } finally {
      setSaving(false)
    }
  }

  const summaryText = useMemo(() => {
    if (maxUsers === null || maxUsers === undefined) {
      return `Usuarios registrados en ${accountName}: ${usersCount} · Límite: Ilimitado`
    }

    return `Usuarios registrados en ${accountName}: ${usersCount} de ${maxUsers}`
  }, [accountName, usersCount, maxUsers])

  return (
    <div className="of-settings-page">
      <div className="of-settings-hero">
        <h1 className="of-settings-hero__title">Usuarios</h1>
        <div className="of-settings-hero__subtitle">
          Agrega usuarios operativos, asígnalos a la clínica activa y mantén
          control sobre el crecimiento del equipo.
        </div>
      </div>

      {!snapshotClinicId && (
        <div className="of-settings-banner of-settings-banner--warning">
          Selecciona una clínica específica para administrar usuarios.
        </div>
      )}

      {isReadOnly && (
        <div className="of-settings-banner of-settings-banner--danger">
          {readOnlyReason || "La clínica está en modo solo lectura."}
        </div>
      )}

      {error && (
        <div className="of-settings-banner of-settings-banner--danger">
          {error}
        </div>
      )}

      {success && (
        <div className="of-settings-banner of-settings-banner--success">
          {success}
        </div>
      )}

      <div className="of-settings-grid">
        <div className="of-settings-card">
          <div className="of-settings-card__title">Capacidad actual</div>
          <div className="of-settings-card__subtitle">
            Estado del plan y usuarios disponibles
          </div>

          <div className="of-settings-summary">
            <div className="of-settings-pill">{summaryText}</div>
            <div className="of-settings-pill">Clínica activa</div>
          </div>
        </div>

        <div className="of-settings-card">
          <div className="of-settings-card__title">Nuevo usuario</div>
          <div className="of-settings-card__subtitle">
            Crea acceso operativo y asígnale un rol principal.
          </div>

          <div className="of-settings-grid--form">
            <div className="of-settings-field">
              <label className="of-settings-label">Nombre</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Nombre"
                className="of-settings-input"
              />
            </div>

            <div className="of-settings-field">
              <label className="of-settings-label">Correo</label>
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Correo"
                className="of-settings-input"
              />
            </div>

            <div className="of-settings-field">
              <label className="of-settings-label">Rol</label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="of-settings-select"
              >
                {roleOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <button
              onClick={handleCreate}
              disabled={!canCreate}
              className="of-settings-button of-settings-button--primary"
            >
              {saving ? "Guardando..." : "Crear"}
            </button>
          </div>

          {limitReached && (
            <div
              className="of-settings-banner of-settings-banner--warning"
              style={{ marginTop: 12, marginBottom: 0 }}
            >
              Has alcanzado el límite de usuarios de tu plan.
            </div>
          )}
        </div>

        {loading ? (
          <div className="of-settings-empty">Cargando...</div>
        ) : users.length === 0 ? (
          <div className="of-settings-empty">
            No hay usuarios registrados en esta clínica todavía.
          </div>
        ) : (
          <div className="of-settings-list">
            {users.map((user) => (
              <div key={user.membershipId} className="of-settings-item">
                <div className="of-settings-item__row">
                  <div>
                    <div className="of-settings-item__title">{user.name}</div>
                    <div className="of-settings-item__meta">{user.email}</div>
                  </div>

                  <div className="of-settings-item__meta">
                    {user.role}
                  </div>

                  <div className="of-settings-item__meta">
                    {user.specialty || ""}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}